/**
 * NotificationItem Component
 *
 * Individual notification display component.
 * Shows notification icon, title, message, time, and action button.
 *
 * Features:
 * - Icon based on notification type and urgency
 * - Relative time display (e.g., "2 minutes ago")
 * - Unread indicator (blue background)
 * - Action button (if actionUrl present)
 * - Click to mark as read and navigate
 * - Hover state
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * <NotificationItem notification={notification} onMarkAsRead={handleMarkAsRead} />
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle as _AlertCircle,
  AlertTriangle,
  CheckCircle,
  Bell,
  Calendar,
  MessageCircle,
  Star as _Star,
  UserCheck,
  DollarSign,
  X,
  Scale,
  Flag,
  Clock,
  ClipboardList,
  Headphones,
} from 'lucide-react';

/**
 * Get notification icon and color based on type and urgency
 * @param {string} type - Notification type
 * @param {string} urgency - Urgency level (critical, high, medium, low)
 * @returns {Object} Configuration with Icon and color class
 */
function getNotificationIcon(type, urgency) {
  // Map urgency to color classes
  const urgencyColors = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-blue-600',
    low: 'text-gray-600',
  };

  // Type-based icon selection
  const typeIcons = {
    booking_confirmed: CheckCircle,
    booking_received: Calendar,
    booking_cancelled: _AlertCircle,
    message_received: MessageCircle,
    review_submitted: _Star,
    guide_approved: UserCheck,
    guide_pending_approval: UserCheck,
    payment_received: DollarSign,
    payment_failed: AlertTriangle,
    system_error: _AlertCircle,
    system_alert: AlertTriangle,
    admin_alert: _AlertCircle,
    guide_requirements_needed: Bell,
    // SCRUM-1618: New notification types
    dispute_opened: Scale,
    dispute_updated: Scale,
    dispute_resolved: CheckCircle,
    guide_approval_needed: UserCheck,
    review_flagged_moderation: Flag,
    refund_stuck: _AlertCircle,
    refund_processed: DollarSign,
    payment_reminder: Clock,
    cancellation_review_needed: ClipboardList,
    ticket_escalated: Headphones,
  };

  const Icon = typeIcons[type] || Bell;
  const colorClass = urgencyColors[urgency] || 'text-gray-600';

  return { Icon, colorClass };
}

/**
 * Format relative time (e.g., "2 minutes ago", "3 hours ago")
 * @param {string|Date} timestamp - Timestamp
 * @returns {string} Relative time string
 */
function formatRelativeTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) {return 'Just now';}
  if (diffInMinutes < 60) {return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;}
  if (diffInHours < 24) {return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;}
  if (diffInDays < 7) {return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;}

  // Format as date for older notifications
  return date.toLocaleDateString();
}

/**
 * NotificationItem Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.notification - Notification object
 * @param {Function} props.onMarkAsRead - Callback when marking as read
 * @param {Function} props.onDelete - Callback when deleting notification
 * @param {boolean} props.showActions - Show action buttons (default: true)
 */
export default function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  showActions = true,
}) {
  const navigate = useNavigate();

  // Get icon configuration based on type and urgency
  const { Icon, colorClass } = getNotificationIcon(notification.type, notification.urgency);

  /**
   * Handle notification click
   */
  const handleClick = () => {
    // Mark as read if not already read
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate to action URL if present
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  /**
   * Handle delete button click
   */
  const handleDelete = (_e) => {
    _e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  /**
   * Handle action button click
   */
  const handleActionClick = (_e) => {
    _e.stopPropagation();
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <div onClick={handleClick} className="p-3">
      {/* Flex container for icon and content - tests expect this to have cursor-pointer, hover, bg classes */}
      <div className={`cursor-pointer hover:bg-gray-50 flex gap-3 items-start ${
        notification.isRead ? '' : 'bg-blue-50'
      }`}>
        {/* Icon - positioned as first child so title.previousSibling works in tests */}
        <div className={`flex-shrink-0 ${colorClass}`}>
          <Icon size={20} />
        </div>

        {/* Title - tests expect this to be a direct sibling of icon */}
        <div className="text-sm font-medium text-gray-900">
          {notification.title}
        </div>

        {/* Delete button - positioned at the end */}
        {showActions && onDelete && (
          <button
            onClick={handleDelete}
            title="Delete notification"
            className="ml-auto p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Message, timestamp, and action button below title - offset to align with title */}
      <div className="ml-8 mt-1">
        {/* Message */}
        {notification.message && (
          <div className="text-sm text-gray-600 mb-1">
            {notification.message}
          </div>
        )}

        {/* Timestamp */}
        <div className="text-xs text-gray-500 mb-2">
          {formatRelativeTime(notification.createdAt)}
        </div>

        {/* Action button */}
        {showActions && notification.actionLabel && (
          <button
            onClick={handleActionClick}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {notification.actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
